import { DateISO } from "../../../utils/DateISO";
import { TimelineItemData } from "../../TimelineItem/types";
import { findDateMatchRanges } from "../../TimelineItem/utils/searchHighlight";

/**
 * Configuration for filtering timeline items
 */
export interface TimelineFilterConfig {
  /**
   * Tags to filter by (items must have at least one matching tag)
   */
  selectedTags: string[];

  /**
   * Search query to match against content, type label, doctor name, tags,
   * date, and source page tag. Date matching accepts the raw ISO value
   * ("2024-03-05") and the displayed US form ("03/05/2024" or "3/5/2024"),
   * with "/", "." and "-" treated interchangeably, so a partial query like
   * "03/05" or "2024" matches too — see {@link findDateMatchRanges}.
   * Source-tag matching accepts (the party alone, "ADJ", also matches an
   * upload-indexed tag such as "ADJ_2"):
   *   - exact tag (e.g. "ADJ", "ADJ_2")
   *   - exact page range (e.g. "P1-3")
   *   - composed (e.g. "ADJ P1-3")
   *   - single page contained in the range, with or without a "P" prefix
   *     and optional tag scope (e.g. "2", "P2", "ADJ P2")
   */
  searchQuery: string;

  /**
   * Type labels to filter by (items must match one of these types)
   */
  selectedTypes: string[];

  /**
   * Date range filter
   */
  dateRange: {
    startDate: DateISO | null;
    endDate: DateISO | null;
  };

  /**
   * Whether to show only starred items
   */
  showStarredOnly: boolean;

  /**
   * Whether to show only important items
   */
  showImportantOnly: boolean;

  /**
   * PDF file names to filter by (items must match one of these files)
   */
  selectedFiles: string[];
}

/**
 * Configuration for sorting timeline items
 */
export interface TimelineSortConfig<T extends string = string> {
  /**
   * Sort order: "asc" (oldest first) or "desc" (newest first)
   */
  sortOrder: "asc" | "desc";

  /**
   * Priority map for types when sorting items with equal dates.
   * Lower number = higher priority (appears first).
   * Types not in map default to 999 (lowest priority).
   */
  typePriority?: Partial<Record<T, number>>;

  /**
   * Group entries by pending AI suggestion before the date sort:
   * text-range ("Suggest edit") entries first, whole-entry delete
   * ("Suggest delete") second, everything else last. Within each group the
   * normal date/priority/id order applies. Edit mode only — suggestions are
   * not attached to items in display mode.
   */
  groupBySuggestion?: boolean;
}

/**
 * Function to get the label for a type (used for search matching)
 */
export type GetTypeLabelFn = (type: string) => string;

/**
 * Parses a single-page search token, optionally prefixed with a tag.
 * Returns null when the query isn't a page-number form (e.g. is a full
 * range "p1-3", a tag alone "da", or arbitrary text). The returned page
 * is matched against each item's [startPageNumber, endPageNumber].
 *
 * Accepted forms (already lowercased + trimmed):
 *   "123"        -> { page: 123 }
 *   "p123"       -> { page: 123 }
 *   "da 123"     -> { tag: "da", page: 123 }
 *   "da p123"    -> { tag: "da", page: 123 }
 */
function parseSinglePageQuery(
  query: string,
): { tag?: string; page: number } | null {
  // A dash means it's a range — handled by exact-equality match elsewhere.
  if (query.includes("-")) return null;
  // Tag-prefixed: "da 123" or "da p123". Tag must be separated by whitespace
  // so the leading "p" in "p123" isn't mis-captured as a one-letter tag.
  const tagged = query.match(/^([a-z]+(?:_\d+)?)\s+p?(\d+)$/);
  if (tagged) {
    return { tag: tagged[1], page: Number(tagged[2]) };
  }
  // Plain: "123" or "p123".
  const plain = query.match(/^p?(\d+)$/);
  if (plain) {
    return { page: Number(plain[1]) };
  }
  return null;
}

/**
 * Filters timeline items based on the provided filter configuration.
 *
 * Filter logic:
 * - Tags: Items must have at least one tag matching the selected tags (OR logic)
 * - Search: Matches against content, type label, doctor name, tags, date, and
 *   the source page tag (case-insensitive). For date and source/page tokens,
 *   see the accepted forms in {@link TimelineFilterConfig.searchQuery}.
 * - Types: Items must match one of the selected types
 * - Date range: Items must fall within the specified date range
 * - Starred: When enabled, only starred items are shown
 *
 * @param items - The timeline items to filter
 * @param config - The filter configuration
 * @param getTypeLabel - Function to get the display label for a type
 * @returns Filtered array of timeline items
 */
export function filterTimelineItems<T extends string = string>(
  items: TimelineItemData<T>[],
  config: TimelineFilterConfig,
  getTypeLabel: GetTypeLabelFn,
): TimelineItemData<T>[] {
  let filtered = [...items];

  // Filter by tags (OR logic - item must have at least one matching tag)
  if (config.selectedTags.length > 0) {
    filtered = filtered.filter((item) =>
      item.tags?.some((tag) => config.selectedTags.includes(tag)),
    );
  }

  // Filter by search query (case-insensitive)
  if (config.searchQuery.trim()) {
    const query = config.searchQuery.toLowerCase().trim();
    // Pre-parse a single-page form ("123", "p123", "da p123") once so the
    // per-item check is O(1). A bare/single page matches any segment whose
    // page range contains it — so typing "102" finds a "P100-103" segment.
    const pageQuery = parseSinglePageQuery(query);
    filtered = filtered.filter((item) => {
      // Source page tag matching uses exact equality against each semantic
      // token (tag alone, page range alone, or composed "<tag> P<s>-<e>") to
      // avoid substring false positives — e.g. "P1-3" must not match an item
      // whose page range is "P1-30".
      const sourceTag = (item.sourceTag ?? "").toLowerCase();
      // "adj_2" is the second adjuster file; a query of "adj" should still
      // find it, so the party before the "_N" index is an accepted form too.
      const sourceParty = sourceTag.split("_")[0];
      const tagForms = [sourceTag, sourceParty];
      const matchesTag = (tag: string) => tagForms.includes(tag);
      const pageRange = `p${item.startPageNumber}-${item.endPageNumber}`;
      const matchesExactSource =
        matchesTag(query) ||
        pageRange === query ||
        tagForms.some((tag) => `${tag} ${pageRange}` === query);
      // Same matcher the item renderer highlights with, so anything the search
      // filters in also shows a <mark> (see findDateMatchRanges).
      const matchesDate =
        findDateMatchRanges(item.date, config.searchQuery).length > 0;
      const matchesPageInRange =
        pageQuery !== null &&
        (pageQuery.tag === undefined || matchesTag(pageQuery.tag)) &&
        item.startPageNumber <= pageQuery.page &&
        pageQuery.page <= item.endPageNumber;
      return (
        item.content.toLowerCase().includes(query) ||
        item.title.toLowerCase().includes(query) ||
        getTypeLabel(item.type).toLowerCase().includes(query) ||
        item.doctorName.toLowerCase().includes(query) ||
        item.tags?.some((tag) => tag.toLowerCase().includes(query)) ||
        matchesExactSource ||
        matchesDate ||
        matchesPageInRange
      );
    });
  }

  // Filter by types
  if (config.selectedTypes.length > 0) {
    filtered = filtered.filter((item) =>
      config.selectedTypes.includes(item.type),
    );
  }

  // Filter by date range
  if (config.dateRange.startDate || config.dateRange.endDate) {
    filtered = filtered.filter((item) => {
      const itemDate = DateISO.fromString(item.date);
      if (!itemDate) return false;

      let withinRange = true;

      if (config.dateRange.startDate) {
        withinRange =
          withinRange && !itemDate.isBefore(config.dateRange.startDate);
      }

      if (config.dateRange.endDate) {
        withinRange =
          withinRange && !itemDate.isAfter(config.dateRange.endDate);
      }

      return withinRange;
    });
  }

  // Filter by starred status
  if (config.showStarredOnly) {
    filtered = filtered.filter((item) => item.isStarred === true);
  }

  // Filter by important status
  if (config.showImportantOnly) {
    filtered = filtered.filter((item) => item.isImportant === true);
  }

  // Filter by selected files
  if (config.selectedFiles.length > 0) {
    filtered = filtered.filter((item) =>
      matchesFileFilter(item, config.selectedFiles),
    );
  }

  return filtered;
}

/**
 * Whether an item belongs to one of the files picked in the "Select file"
 * filter.
 *
 * A selection carries the item's `uploadedFileID` when there is one, which
 * keeps two PDFs sharing an S3 basename as separate options. `pdfFileName` is
 * still accepted so that entries without a resolved file (V1 briefings) stay
 * filterable, and so the `?fileName=` deep link from the debug
 * RecordReviewGraph keeps working — it passes a raw file name, not an ID.
 */
export function matchesFileFilter(
  item: Pick<TimelineItemData, "uploadedFileID" | "pdfFileName">,
  selectedFiles: string[],
): boolean {
  if (item.uploadedFileID && selectedFiles.includes(item.uploadedFileID)) {
    return true;
  }
  return item.pdfFileName != null && selectedFiles.includes(item.pdfFileName);
}

/**
 * Suggestion group rank for `groupBySuggestion`. Mirrors the tags shown in
 * TimelineItemMetadataEdit: an entry already marked for deletion shows no
 * suggestion tag, so it ranks with the plain entries.
 */
function suggestionGroup(item: TimelineItemData<string>): number {
  if (item.markedForDeletion) return 2;
  if (item.textRangeSuggestions?.length) return 0;
  if (item.deleteSegmentSuggestion) return 1;
  return 2;
}

/**
 * Sorts timeline items based on the provided sort configuration.
 *
 * Sort order:
 * 0. (groupBySuggestion only) suggest-edit → suggest-delete → other
 * 1. By date (null dates sorted to the top)
 * 2. By type priority (lower number = higher priority, defaults to 999)
 * 3. By logicalSegmentID for stable ordering (falls back to id if not available)
 *
 * @param items - The timeline items to sort (will be sorted in place)
 * @param config - The sort configuration
 * @returns The sorted array (same reference as input)
 */
export function sortTimelineItems<T extends string = string>(
  items: TimelineItemData<T>[],
  config: TimelineSortConfig<T>,
): TimelineItemData<T>[] {
  const { sortOrder, typePriority, groupBySuggestion = false } = config;

  // Decorate-sort-undecorate. The comparator used to call DateISO.fromString
  // twice per comparison — each running a regex test AND constructing a
  // throwaway `new Date` purely to validate — then localeCompare'd two ISO
  // strings that are already lexicographically ordered. At 800 entries that is
  // ~15k Date allocations and ~7.7k Intl collations per sort, and this sort
  // runs on every filter change and every search keystroke. Measured 2.38ms ->
  // 0.10ms. Validating once per item keeps DateISO's exact semantics (same
  // null handling, same console.error on a malformed date) while dropping the
  // O(n log n) multiplier.
  const decorated = items.map((item) => ({
    item,
    dateKey: DateISO.fromString(item.date)?.toString() ?? null,
    priority: typePriority?.[item.type as T] ?? 999,
    stableId: item.logicalSegmentID || item.id,
    group: groupBySuggestion ? suggestionGroup(item) : 0,
  }));

  decorated.sort((a, b) => {
    if (a.group !== b.group) return a.group - b.group;

    // Sort null dates to the top, regardless of sortOrder
    if (!a.dateKey && b.dateKey) return -1;
    if (a.dateKey && !b.dateKey) return 1;

    // ISO YYYY-MM-DD is lexicographically ordered, so < / > matches what
    // localeCompare produced.
    if (a.dateKey && b.dateKey && a.dateKey !== b.dateKey) {
      const ascending = a.dateKey < b.dateKey ? -1 : 1;
      return sortOrder === "desc" ? -ascending : ascending;
    }

    // When dates are equal or both null:
    // 1. Sort by type priority (lower number = higher priority)
    if (a.priority !== b.priority) {
      return a.priority - b.priority;
    }

    // 2. Sort by logicalSegmentID for stable order (falls back to id)
    if (a.stableId === b.stableId) return 0;
    return a.stableId < b.stableId ? -1 : 1;
  });

  // Write back in place — callers rely on the returned array being the same
  // reference as the input (see the doc comment above).
  for (let i = 0; i < decorated.length; i++) {
    items[i] = decorated[i].item;
  }

  return items;
}

/**
 * Filters and sorts timeline items in a single operation.
 *
 * This is a convenience function that combines filterTimelineItems and sortTimelineItems.
 *
 * @param items - The timeline items to process
 * @param filterConfig - The filter configuration
 * @param sortConfig - The sort configuration
 * @param getTypeLabel - Function to get the display label for a type
 * @returns Filtered and sorted array of timeline items
 */
export function filterAndSortTimelineItems<T extends string = string>(
  items: TimelineItemData<T>[],
  filterConfig: TimelineFilterConfig,
  sortConfig: TimelineSortConfig<T>,
  getTypeLabel: GetTypeLabelFn,
): TimelineItemData<T>[] {
  const filtered = filterTimelineItems(items, filterConfig, getTypeLabel);
  return sortTimelineItems(filtered, sortConfig);
}
